<template>
  <section
    class="office-card"
    v-observe-visibility="{callback: animation, once: true}"
  >
    <div class="header">
      <transition name="fade-down">
        <h3
          class="title"
          v-show="inView"
        >{{content?.City}}, {{content?.Country}}</h3>
      </transition>
      <transition name="draw-line">
        <div
          class="line"
          v-show="inView"
        ></div>
      </transition>
    </div>
    <transition name="fade-down">
      <ul
        class="contacts"
        v-show="inView"
      >
        <li class="contact">
          <Link
            :tag="'a'"
            :href="`tel:${content?.Phone?.replace(/ /gi, '')}`"
          >{{content?.Phone}}</Link>
        </li>
        <li class="contact">
          <Link
            :tag="'a'"
            :href="`mailto:${content?.Email}`"
          >{{content?.Email}}
          </Link>
        </li>
      </ul>
    </transition>
    <div class="bottom">
      <transition name="fade-down">
        <div
          class="bottom-content"
          v-show="inView"
        >
          <div class="address">{{content?.Address}}</div>
          <div class="location-link">
            <transition name="draw-line">
              <div
                class="line"
                v-if="breakpoint === 'sm'"
                v-show="inView"
              ></div>
            </transition>
            <Link
              :tag="'a'"
              :href="content?.Link"
              target="_blank"
            >
            <template #default>
              {{ linkName }}
            </template>
            <template #end>
              <Icon name="pin-outline" />
            </template>
            </Link>
          </div>
        </div>
      </transition>
      <transition name="draw-line">
        <div
          class="line"
          v-if="breakpoint !== 'sm'"
          v-show="inView"
        ></div>
      </transition>
    </div>
  </section>
</template>

<script>
import Link from '../../../components/Link.vue';
import Icon from '../../../components/Icon.vue';
import { animationMixin } from '../../../components/mixins/animation';

export default {
  components: { Link, Icon },
  mixins: [animationMixin],
  name: 'OfficeCard',
  props: {
    content: {
      type: Object,
    },
    linkName: {
      type: String,
    },
  },
  computed: {
    breakpoint() {
      return this.$store.getters.getBreakpoint;
    },
  },
};
</script>

<style lang="scss" scoped>
.office-card {
  .header {
    display: flex;
    align-items: center;
  }

  ::v-deep(.title) {
    display: inline-block;
    flex: none;
    font-weight: 700;
    line-height: 1.72;
    letter-spacing: -0.02em;

    @include wRule(
      font-size,
      (
        xl: 30,
        lg: 25,
        md: 25,
        sm: 30,
      )
    );

    &:not(:last-child) {
      @include wRule(
        margin-right,
        (
          xl: 28,
          lg: 35,
          md: 20,
          sm: 25,
        )
      );
    }

    &.fade-down-enter-active,
    &.fade-down-leave-active {
      transition-delay: 0.3s;
    }
  }

  .line {
    display: block;
    flex: 1;
    background-color: currentColor;
    transform-origin: 100% 50%;

    @include wRule(
      height,
      (
        xl: 3,
        lg: 3,
        md: 3,
        sm: 3,
      )
    );

    &.draw-line-enter-active,
    &.draw-line-leave-active {
      transition-delay: 0.3s;
    }
  }

  .contacts {
    display: block;
    width: 100%;

    @include wRule(
      margin-top,
      (
        xl: 4,
        lg: 4,
        md: 4,
        sm: 4,
      )
    );

    &.fade-down-enter-active,
    &.fade-down-leave-active {
      transition-delay: 0.4s;
    }
  }

  .contact {
    display: block;
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: -0.02em;

    @include wRule(
      font-size,
      (
        xl: 30,
        lg: 25,
        md: 25,
        sm: 30,
      )
    );

    &:not(:first-child) {
      @include wRule(
        margin-top,
        (
          xl: 12,
          lg: 8,
          md: 8,
          sm: 12,
        )
      );
    }
  }

  .bottom {
    display: block;

    &:not(:first-child) {
      @include wRule(
        margin-top,
        (
          xl: 114,
          lg: 94,
          md: 72,
          sm: 85,
        )
      );
    }

    .line {
      flex: none;
      width: 100%;
      transform-origin: 0 50%;

      @include screen("sm", max) {
        flex: 1;
        width: auto;
      }

      @include wRule(
        margin-top,
        (
          xl: 32,
          lg: 14,
          md: 8,
          sm: 0,
        )
      );

      &.draw-line-enter-active,
      &.draw-line-leave-active {
        transition-delay: 0.6s;
      }
    }
  }

  .bottom-content {
    display: flex;
    align-items: center;
    line-height: 1.5;

    @include rule(
      justify-content,
      (
        xl: space-between,
        lg: flex-start,
      )
    );

    @include rule(
      align-items,
      (
        xl: center,
        lg: flex-start,
      )
    );

    @include rule(
      flex-direction,
      (
        xl: row,
        lg: column,
      )
    );

    @include wRule(
      font-size,
      (
        xl: 20,
        lg: 19,
        md: 16,
        sm: 30,
      )
    );

    &.fade-down-enter-active,
    &.fade-down-leave-active {
      transition-delay: 0.5s;
    }
  }

  .address {
    @include rule(
      width,
      (
        lg: 100%,
      )
    );
  }

  .location-link {
    font-weight: 700;
    letter-spacing: -0.02em;

    @include screen("sm") {
      display: flex;
      align-items: center;
      width: 100%;
    }

    @include wRule(
      margin-top,
      (
        lg: 14,
        md: 18,
        sm: 32,
      )
    );

    .link {
      flex: none;
    }

    .line {
      transform-origin: 0 50%;

      &:not(:last-child) {
        @include wRule(
          margin-right,
          (
            sm: 30,
          )
        );
      }

      &.draw-line-enter-active,
      &.draw-line-leave-active {
        transition-delay: 0.6s;
      }
    }
  }
}
</style>
